import type { SignalRow } from '@bloo-team/api-client'
import { DEMO_CHAIN_AGO, DEMO_NOW } from '@bloo-team/core-shell'

/**
 * The SIGNALS demo fixture (decision S12): ~200 signals over 7 days, six
 * AUTHORED attack chains (storyline candidates) plus deterministic
 * noise (single-detection chatter that should not). Row shape is the
 * lowercase stream vocabulary (lib/search.ts DEMO_SIGNAL_FIELDS) extended
 * with the workbook-side fields a full `/signals/data` document carries
 * (`DetectionScore`, `raw_event_query`, `tag_list`) — both spellings on
 * purpose, so readSignal is exercised on the mixed case the live backend
 * produces.
 *
 * Storylines are the connected-signals subgraphs lib/connected-signals.ts builds
 * over these rows; the authored chains are not guaranteed one storyline each.
 *
 * Times are relative to `base` (default: module load) so the 1d/7d/30d
 * windows always contain data. Pass a fixed base in tests.
 */

interface DemoDetection {
  id: string
  name: string
  tactic: string
  technique: string
  score: number
  streams: string[]
  query: (e: Record<string, string>) => string
}

const D: Record<string, DemoDetection> = {
  failedLogins: {
    id: 'unusual-spike-in-failed-login-attempts',
    name: 'Unusual Spike in Failed Login Attempts',
    tactic: 'Credential Access',
    technique: 'Brute Force',
    score: 5,
    streams: ['WIN-AUDIT'],
    query: (e) => `stream=WIN-AUDIT where EID = 4625 AND User = '${e.user}' | duration 1h | limit 200`,
  },
  logonType10: {
    id: 'interactive-logon-from-new-source',
    name: 'Interactive logon from new source',
    tactic: 'Initial Access',
    technique: 'Valid Accounts',
    score: 4,
    streams: ['WIN-AUDIT'],
    query: (e) => `stream=WIN-AUDIT where EID = 4624 AND User = '${e.user}' | duration 1h | limit 100`,
  },
  powershell: {
    id: 'suspicious-powershell-execution',
    name: 'Suspicious PowerShell Execution',
    tactic: 'Execution',
    technique: 'Command and Scripting Interpreter',
    score: 7,
    streams: ['EP-PROCESS'],
    query: (e) => `stream=EP-PROCESS where System = '${e.host}' AND Image LIKE '%powershell%' | duration 1h | limit 100`,
  },
  serviceInstall: {
    id: 'service-installed-from-temp-path',
    name: 'Service installed from temp path',
    tactic: 'Persistence',
    technique: 'Create or Modify System Process',
    score: 8,
    streams: ['WIN-AUDIT'],
    query: (e) => `stream=WIN-AUDIT where EID = 7045 AND System = '${e.host}' | duration 1h | limit 50`,
  },
  lsass: {
    id: 'credential-dump-via-lsass-access',
    name: 'Credential dump via LSASS access',
    tactic: 'Credential Access',
    technique: 'OS Credential Dumping',
    score: 10,
    streams: ['EP-PROCESS'],
    query: (e) => `stream=EP-PROCESS where System = '${e.host}' AND TargetImage LIKE '%lsass.exe' | duration 1h | limit 50`,
  },
  adminShare: {
    id: 'execute-command-writing-output-to-local-admin-share',
    name: 'Execute command writing output to local Admin Share',
    tactic: 'Lateral Movement',
    technique: 'SMB/Windows Admin Shares',
    score: 8,
    streams: ['EP-PROCESS', 'WIN-AUDIT'],
    query: (e) => `stream=EP-PROCESS where System = '${e.host}' AND CommandLine LIKE '%ADMIN$%' | duration 1h | limit 50`,
  },
  rareAsn: {
    id: 'outbound-connection-to-rare-asn',
    name: 'Outbound connection to rare ASN',
    tactic: 'Command and Control',
    technique: 'Application Layer Protocol',
    score: 7,
    streams: ['FIREWALL'],
    query: (e) => `stream=FIREWALL where SrcIP = '${e.ip}' | duration 1h | limit 100`,
  },
  schedTask: {
    id: 'scheduled-task-persistence',
    name: 'Scheduled task persistence',
    tactic: 'Persistence',
    technique: 'Scheduled Task/Job',
    score: 5,
    streams: ['WIN-AUDIT'],
    query: (e) => `stream=WIN-AUDIT where EID = 4698 AND System = '${e.host}' | duration 1h | limit 50`,
  },
  portSweep: {
    id: 'internal-port-sweep',
    name: 'Internal Port Sweep',
    tactic: 'Discovery',
    technique: 'Network Service Discovery',
    score: 4,
    streams: ['FIREWALL'],
    query: (e) => `stream=FIREWALL where SrcIP = '${e.ip}' | duration 1h | limit 200`,
  },
  largeUpload: {
    id: 'large-outbound-transfer',
    name: 'Large outbound transfer',
    tactic: 'Exfiltration',
    technique: 'Exfiltration Over C2 Channel',
    score: 9,
    streams: ['FIREWALL'],
    query: (e) => `stream=FIREWALL where SrcIP = '${e.ip}' AND TXLen > 5000 | duration 1h | limit 100`,
  },
  loginOutside: {
    id: 'login-outside-in',
    name: 'Login outside IN',
    tactic: 'Initial Access',
    technique: 'Valid Accounts',
    score: 3,
    streams: ['WIN-AUDIT'],
    query: (e) => `stream=WIN-AUDIT where User = '${e.user}' | duration 1h | limit 100`,
  },
  dnsTunnel: {
    id: 'dns-query-entropy',
    name: 'High-entropy DNS queries',
    tactic: 'Command and Control',
    technique: 'DNS',
    score: 6,
    streams: ['DNS'],
    query: (e) => `stream=DNS where System = '${e.host}' | duration 1h | limit 200`,
  },
  vpnGeo: {
    id: 'vpn-login-new-country',
    name: 'VPN login from new country',
    tactic: 'Initial Access',
    technique: 'External Remote Services',
    score: 5,
    streams: ['WIN-AUDIT'],
    query: (e) => `stream=WIN-AUDIT where User = '${e.user}' AND EID = 4624 | duration 1h | limit 50`,
  },
}

const MIN = 60_000

/** Page-load anchor shared by the signals fixture and the EP-PROCESS search fixture. */
export { DEMO_CHAIN_AGO, DEMO_NOW }

/**
 * Where each authored chain's endpoint sits in time (minutes before
 * DEMO_NOW) — lib/search.ts places the EP-PROCESS rows here so the deep
 * panel's ±15 min window finds them. Keep in sync with chains() below.
 */

interface Emit {
  d: DemoDetection
  /** Minutes before base. */
  ago: number
  suspect?: Record<string, string>
  target?: Record<string, string>
  occurrences?: number
  fp?: boolean
  remark?: string
}

/**
 * Detection stage per demo detection (`$Stage`, workbook/meta values) — mostly
 * prod, a few in beta / dev, one with no stage, so the Stage filter has
 * something to narrow.
 */
const DEMO_STAGE: Record<string, string | null> = {
  'dns-query-entropy': 'beta',
  'vpn-login-new-country': 'beta',
  'internal-port-sweep': 'dev',
  'login-outside-in': null,
}

function row(i: number, e: Emit, base: number): SignalRow {
  const ents: Record<string, string> = {}
  const q: Record<string, string> = {}
  for (const [k, v] of Object.entries(e.suspect ?? {})) {
    ents[`suspect${k}`] = v
    q[k] = v
  }
  for (const [k, v] of Object.entries(e.target ?? {})) {
    ents[`target${k}`] = v
    q[k] = q[k] ?? v
  }
  const sev = e.d.score >= 10 ? 'Critical' : e.d.score >= 7 ? 'High' : e.d.score >= 4 ? 'Medium' : 'Low'
  return {
    cnamtime: base - Math.round(e.ago * MIN),
    sourcetype: 'Workbook',
    uuid: `sig-${i.toString().padStart(4, '0')}`,
    scope: 'gotham',
    detectionseverity: sev,
    detectionname: e.d.name,
    detectiontactic: e.d.tactic,
    DetectionTechnique: e.d.technique,
    DetectionScore: e.d.score,
    DetectionConfidence: e.d.score >= 8 ? 'High' : 'Medium',
    Workbook: e.d.id,
    Stage: e.d.id in DEMO_STAGE ? DEMO_STAGE[e.d.id]! : 'prod',
    occurrences: e.occurrences ?? 1,
    falsepositive: e.fp ?? false,
    sourcestream: JSON.stringify(e.d.streams),
    tag_list: JSON.stringify([e.d.tactic.toLowerCase().replace(/\s+/g, '-')]),
    raw_event_query: e.d.query(q),
    signal_remark: e.remark ?? null,
    suspecthash: null,
    suspecturl: null,
    suspectlocation: null,
    targetresource: null,
    ...ents,
  }
}

/** Tiny LCG so the noise is deterministic without Math.random. */
function lcg(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0xffffffff
  }
}

/**
 * Six authored attack chains, anchored on the entity named in the comment.
 * Storylines are the connected-signals subgraphs the port (lib/connected-signals.ts)
 * builds over these rows, so a chain is no longer guaranteed one storyline each
 * (the IP chain is absent; m.chen joins the dc-01 noise).
 */
function chains(): Emit[] {
  const out: Emit[] = []
  // 1. fin-srv-07 — full chain: new logon -> service -> LSASS -> C2 -> sched task (~40 min ago)
  out.push(
    { d: D.logonType10!, ago: 49, suspect: { user: 'j.mendes' }, target: { host: 'fin-srv-07' } },
    { d: D.serviceInstall!, ago: 42, suspect: { user: 'svc_backup' }, target: { host: 'fin-srv-07' } },
    { d: D.lsass!, ago: 40, suspect: { user: 'j.mendes', hash: '9f2c1a77b0d54e0e8c3b1e41a' }, target: { host: 'fin-srv-07' }, occurrences: 3 },
    { d: D.rareAsn!, ago: 38, suspect: { ip: '10.20.4.17' }, target: { host: 'fin-srv-07', url: '185.220.101.44' } },
    { d: D.schedTask!, ago: 11, suspect: { user: 'svc_backup' }, target: { host: 'fin-srv-07' } },
  )
  // 2. r.okafor — VPN from new country, then failed logins, then lateral (3h ago)
  out.push(
    { d: D.vpnGeo!, ago: 190, suspect: { user: 'r.okafor' }, target: { host: 'vpn-gw-2' } },
    { d: D.failedLogins!, ago: 175, suspect: { user: 'r.okafor' }, target: { host: 'lt-okafor' }, occurrences: 41 },
    { d: D.adminShare!, ago: 160, suspect: { user: 'r.okafor' }, target: { host: 'hr-fs-01' } },
  )
  // 3. hash 3b7e… seen on two hosts — powershell + admin share (yesterday)
  out.push(
    { d: D.powershell!, ago: 26 * 60, suspect: { user: 'a.silva', hash: '3b7e0c9d2f1a4b5c6d7e8f90' }, target: { host: 'eng-ws-22' } },
    { d: D.powershell!, ago: 25 * 60 + 40, suspect: { user: 'a.silva', hash: '3b7e0c9d2f1a4b5c6d7e8f90' }, target: { host: 'eng-ws-31' } },
    { d: D.adminShare!, ago: 25 * 60 + 10, suspect: { user: 'a.silva' }, target: { host: 'eng-ws-31' } },
  )
  // 4. 10.30.8.9 — port sweep then large upload (2 days ago)
  out.push(
    { d: D.portSweep!, ago: 50 * 60, suspect: { ip: '10.30.8.9' }, target: { host: 'core-sw-1' }, occurrences: 212 },
    { d: D.dnsTunnel!, ago: 49 * 60, suspect: { ip: '10.30.8.9' }, target: { host: 'resolver-01' }, occurrences: 88 },
    { d: D.largeUpload!, ago: 48 * 60, suspect: { ip: '10.30.8.9' }, target: { url: 'files.transfer-cdn.net' } },
  )
  // 5. m.chen — login outside IN + failed logins + interactive logon (4 days ago)
  out.push(
    { d: D.loginOutside!, ago: 4 * 24 * 60 + 30, suspect: { user: 'm.chen', location: 'DE' }, target: { host: 'mail-gw-1' } },
    { d: D.failedLogins!, ago: 4 * 24 * 60 + 20, suspect: { user: 'm.chen' }, target: { host: 'dc-01' }, occurrences: 23 },
    { d: D.logonType10!, ago: 4 * 24 * 60 + 5, suspect: { user: 'm.chen' }, target: { host: 'dc-01' } },
  )
  // 6. build-agent-13 — noisy but closed history: sched task ×N, one powershell (6 days ago)
  out.push(
    { d: D.schedTask!, ago: 6 * 24 * 60, suspect: { user: 'ci-runner' }, target: { host: 'build-agent-13' }, occurrences: 41 },
    { d: D.powershell!, ago: 6 * 24 * 60 - 15, suspect: { user: 'ci-runner' }, target: { host: 'build-agent-13' } },
    { d: D.serviceInstall!, ago: 6 * 24 * 60 - 30, suspect: { user: 'ci-runner' }, target: { host: 'build-agent-13' } },
  )
  return out
}

/**
 * Noise is spread THIN on purpose: many hosts, each chattering on one or two
 * detections, so no noise entity accumulates a storyline-sized score. The
 * authored chains above must sort first — that is what the queue demos.
 */
const FIRST = ['j', 'p', 'l', 'k', 'n', 'b', 'h', 'c', 'o', 'd', 'e', 's']
const LAST = ['smith', 'novak', 'ahmed', 'tanaka', 'ivanov', 'reyes', 'ito', 'dubois', 'mensah', 'costa', 'berg', 'khan']
/** One user per noise host, so no user accumulates across hosts either. */
const NOISE_USERS = [
  'svc-backup', 'administrator', 'deploy-bot',
  ...FIRST.flatMap((f) => LAST.map((l) => `${f}.${l}`)).filter((_, i) => i % 3 === 0),
]
const NOISE_HOSTS = [
  'dc-01', 'dc-02', 'edge-fw-01', 'scan-vault-01', 'nessus-01', 'app-srv-3',
  ...Array.from({ length: 54 }, (_, i) => `ws-${101 + i}`),
]
const NOISE_IPS = ['10.0.3.14', '10.0.7.201', '10.1.1.9', '192.168.2.44', '10.0.5.77', '10.2.0.18']
const NOISE_DETS = [D.failedLogins!, D.loginOutside!, D.portSweep!, D.schedTask!, D.logonType10!, D.dnsTunnel!, D.vpnGeo!, D.powershell!]

function noise(n: number): Emit[] {
  const rnd = lcg(20260824)
  const out: Emit[] = []
  for (let i = 0; i < n; i++) {
    const hostIdx = Math.floor(rnd() * NOISE_HOSTS.length)
    const host = NOISE_HOSTS[hostIdx]!
    // A host's chatter comes from its own pair of detections.
    const pair = [NOISE_DETS[hostIdx % NOISE_DETS.length]!, NOISE_DETS[(hostIdx * 3 + 1) % NOISE_DETS.length]!]
    const d = pair[rnd() < 0.75 ? 0 : 1]!
    const ago = Math.floor(rnd() * 7 * 24 * 60)
    const user = NOISE_USERS[hostIdx % NOISE_USERS.length]!
    const ip = NOISE_IPS[hostIdx % NOISE_IPS.length]!
    const byIp = d === D.portSweep || d === D.dnsTunnel
    const fp = rnd() < 0.08
    out.push({
      d,
      ago,
      suspect: byIp ? { ip } : { user },
      target: { host },
      occurrences: 1 + Math.floor(rnd() * (d === D.failedLogins ? 30 : 4)),
      fp,
      remark: fp ? 'tagged FP by night shift' : undefined,
    })
  }
  return out
}

/** Build the fixture. Sorted newest first, like the list endpoint. */
export function demoSignalRows(base: number = Date.now(), noiseCount = 180): SignalRow[] {
  const emits = [...chains(), ...noise(noiseCount)]
  return emits
    .map((e, i) => row(i + 1, e, base))
    .sort((a, b) => Number(b.cnamtime) - Number(a.cnamtime))
}

export const DEMO_SIGNAL_DETECTIONS = Object.values(D).map((d) => ({ id: d.id, name: d.name, tactic: d.tactic }))
